import styles from './metric-indicator.module.scss';

/**
 * The states the backend pre-thresholds a metric into (`metrics.indicators.*`). Only `healthy` is
 * attested on the deployed API and only `healthy` is drawn in Figma (M8); the other two are the
 * plausible remainder, and an unrecognised value is handled at the schema boundary rather than here.
 */
export const METRIC_INDICATOR_STATES = ['healthy', 'warning', 'critical'] as const;

export type MetricIndicatorState = (typeof METRIC_INDICATOR_STATES)[number];

/** Named states, for the visually-hidden text. Wording is FE-drafted — no criterion supplies it. */
const STATE_TEXT: Record<MetricIndicatorState, string> = {
  healthy: 'Healthy',
  warning: 'Warning',
  critical: 'Critical',
};

export type MetricIndicatorProps = { state: MetricIndicatorState };

/**
 * A metric's state, conveyed **three ways at once**: a distinct shape, a distinct colour, and text
 * only a screen reader sees (113588 §7e; grilling Q13, locked decision 13).
 *
 * Colour alone fails WCAG 1.4.1, and a tooltip is not a fix — it is hover-only, so it leaves sighted
 * keyboard and touch users with nothing. Shape carries the state for them, the hidden text carries it
 * for AT, and neither needs an interaction.
 *
 * **This component defines the presentation, and the Device Details page adopts it.** §17 asks the
 * panel to take everything from that page; the page's own metrics row (`<DeviceMetricsRow>`,
 * US-112437) still draws a colour-only dot, so §17b's "the two agree exactly" is satisfied by that
 * row consuming this component in a follow-up rather than by the panel copying a rule that fails an
 * audit. Where it lives — the shared library, not the `devices` feature — is what makes that a
 * one-line change there.
 *
 * `data-state` is what the tests assert against, so an assertion is against the rule rather than a
 * CSS-module class hash.
 */
export function MetricIndicator({ state }: MetricIndicatorProps) {
  return (
    <span className={styles.indicator}>
      <span className={`${styles.mark} ${styles[state]}`} data-state={state} aria-hidden />
      <span className={styles.state}>{STATE_TEXT[state]}</span>
    </span>
  );
}
